// THE BEAM FLIGHT (PC; APP_STARTUP §7, owner 2026-10-31): on the desktop the
// ⤳ Beam is a camera move instead of the beam-in overlay. The 2D Map zooms in
// on the beam point (FlightCover) until its scale is that of a camera looking
// straight down from H above the beam ground; the 3D render at that framing
// crossfades in over it; the camera swings out of top-down and glides in on
// the beam point from behind the landing heading (flightPath; owner: "not
// straight down" — crossed sprite fans read as stripes from above). Its speed
// is a closed loop on readiness: a pose is flown only once flightTest passed
// it (no white, no node or texture coarser than the pose needs, no building
// tile in view still missing) — a fast GPU lands quickly, a slow one later;
// from the old overlay's release the rest is hurried (control never later
// than the overlay gave it). Control is main.js's, at STAND. The pure parts
// live here (modes, path, pace, test, the 2D cover); main.js wires the stages
// (enterAt, tick: flightCam / flightStage). VR lands too (vrPose: straight
// down, the rig translated only, the optic flow capped — vrVmax).
//
// H: h_m (950) above the beam ground, raised to the near sprite range +
// sprite_pad where that is farther (top-down never over a sprite fan) —
// inside ground..+1 km on every class but the largest sprite range; the view
// from there is the ground below (no far summit is in a top-down frame), so a
// prune for the reachable volume (OWNER_RULES far-visibility-prune) hides
// nothing in it; the 1 km cap is the jet suit's (owner 2026-10-31).
import * as THREE from 'three';
import { drawSet, newState, LOD_M } from '../../groundm/morph.js';
import { nodeSide } from '../../groundm/lookup.js';
import { vtParent } from './gmtex.js';

// the flight per device class: 'flight' (the PC descent), 'vrflight' (the
// headset's: vrPose) or 'overlay' (the beam-in overlay); tunables flight.vr
// 'overlay' is VR's instant fallback (the black headset until the release)
export const FLIGHT_MODES = Object.freeze({ flat: 'flight', mobile: 'overlay', vr: 'vrflight' });
export function flightMode(entryMode, t = null) {
  const m = FLIGHT_MODES[entryMode] || 'overlay';
  return m === 'vrflight' && t && t.vr === 'overlay' ? 'overlay' : m;
}
// the VR landing per player: the comfort menu's pick (vrui, localStorage
// vrm.vr.beam 'fly' | 'instant') wins; none: the world's tunable (flight.vr)
export function vrBeam(pick, t) {
  return pick === 'fly' ? 'flight' : pick === 'instant' ? 'overlay' : t && t.vr === 'overlay' ? 'overlay' : 'flight';
}

// tunables.json `flight` overrides these (no world coordinates)
export const FLIGHT_DEF = Object.freeze({
  h_m: 950,              // the start height above the beam ground (m); at least the sprite range N + sprite_pad
  sprite_pad: 100,       // top-down only this far above N (handoff nakedNear): no crossed sprite fan seen from above
  h_fade_min: 250,       // the 2D stage's floor (raised to N + sprite_pad, at most H)
  glide_deg: 35,         // the approach's depression at most once the view reaches N (steeper: the fans read as stripes)
  turn_min: 0.06,        // the swing out of top-down spans at least this much s
  flare_m: 20,           // below it the view eases level to the landing look
  clear_m: 40,           // the path's clearance over the ground's upper bound (a later ridge lifts it before it)
  open_m: 3000, open_n: 16,   // the landing heading: the most open of open_n azimuths within open_m
  zoom_ms: 1600,         // the 2D Map's zoom onto the beam point
  fade_ms: 700,          // the 2D → 3D crossfade
  vmax: 0.2,             // s per second at most (≥ 5 s descent)
  accel: 0.25,           // s per second² (ease in; braking toward the readiness bound)
  hurry_ms: 2500,        // from the old overlay's release the bound is lifted: the rest of the descent within this
  lead: 'landing',       // the 2D stage selects + paints for the landing view ('camera': for the hidden 3D camera)
  test_ms: 90,           // one readiness test per this long at most
  step0: 0.04, step_min: 0.004, step_max: 0.25,   // the probe's step above the bound (s)
  m_tol: 0.1,            // a missing child whose geomorph would start above this m is coarse
  tex_slack: 0,          // levels of texture blur allowed: a number or { <device class>: n, default: n }
  rays_x: 7, rays_y: 5,  // the test's screen grid (+ the centre)
  ray_max_m: 6000,       // past it a ray counts as horizon (the top level everywhere: THE TOP GATE)
  hitch_ms: 50,          // a frame gap above it moves the camera by nothing (a pause, never a jump)
  dt_max: 1 / 30,        // the flight clock's step at most
  log_max: 4000,         // flight record entries kept (__vr.flight)
  // THE ARC (an in-game beam, PC; owner 2026-10-31 "a high parabolic effect, if fps permit")
  arc_min_m: 150,        // a shorter beam is a hop (below)
  arc_k: 0.35,           // the apex over the straight line: arc_k x the distance, within arc_h_min..arc_h_max (m)
  arc_h_min: 120, arc_h_max: 1200,
  arc_flow_m: 4,         // the arc's pace is even in apparent motion: path length over (the height + arc_flow_m)
  arc_flow_max: 2.5,     // that apparent motion's rate at most (rad/s): the arc's duration grows past arc_dur_* where it needs
  // THE TIMED PACE (the arc, the hop, the entry's descent from the crossfade; owner 2026-10-31 "i see how it starts,
  // then zack, done … i wouldn't mind watching it 2 seconds longer … or even 5 seconds more, depending on the distance"):
  // it moves from the first frame over its duration; readiness only slows it to pace_floor x its base speed
  arc_dur_min_ms: 2000,  // the arc's duration: arc_dur_min_ms + arc_dur_per_km_ms per km past arc_min_m, at most arc_dur_max_ms
  arc_dur_per_km_ms: 2500, arc_dur_max_ms: 7000,
  entry_dur_ms: 5000,    // the entry's descent, the crossfade to the feet
  pace_floor: 0.35,      // the camera ahead of the readiness bound moves at this x its base speed (never stops)
  ramp_ms: 600,          // ease in from rest / brake into the landing (inside the duration)
  arc_max_ms: 8000,      // from here the arc's readiness bound goes: the rest at the base speed
  arc_fps: 30,           // the frame rate (smoothed) under which the arc flies at arc_res x the resolution (look.js LookScale; back after control)
  arc_res: 0.7,
  // THE HOP (a beam under arc_min_m): hop_ms long, apex hop_k x the distance within hop_h_min..hop_h_max, hop_clear_m over the ground
  hop_ms: 1300, hop_k: 0.2, hop_h_min: 6, hop_h_max: 30, hop_clear_m: 4,
  // the flight's input (flat; owner 2026-10-31 "exiting to 2d map … needs to be stopped until the beaming is completed")
  skip_hold_ms: 700,     // Space held this long lands at once (a stray key or click does not)
  look_max_deg: 60,      // the mouse look's offset from the path's look at most
  look_rest_ms: 250,     // the mouse resting this long: the offset springs back
  look_spring: 4,        // ... at this rate (1/s)
  hand_s: 0.8,           // the body's drift from the flight's base joins the camera over s hand_s..1 (handRel)
  res_settle_ms: 1000,   // the arc's resolution back no sooner than this after control (resBack) ...
  res_still_ms: 400,     // ... once the view has stood still this long ...
  res_max_ms: 6000,      // ... or this after control at the latest
  res_ramp_ms: 600,      // then the scale ramps to 1 over this
  // THE CHARGE + THE FILL (beamcharge.js; owner 2026-10-31 "something semi-manual is best")
  charge_ms: 1200,       // the ⤳ Beam's charge: pressed or clicked, it launches itself after this (the landing preloads from the press)
  map_rise_ms: 2500,     // THE INVERSE BEAM: 3D → Map, the rise from the eye to top-down (then the Map fades in)
  map_drop_ms: 2000,     // Map → 3D: the descent onto you after the cover's zoom + crossfade
  col_h_m: 300,          // the light column at the target
  label_cap: 6,          // the flight's place names at most (the 3D view's own labels: peaks, lakes, places)
  // THE MAP WAIT (the entry's 2D stage; beamlabels.js; owner 2026-10-31 "it should show something on the display … entertaining")
  wait_wide: 3,          // the zoom ends this x wider than the matched scale; the hold pushes in toward it ...
  wait_tau_ms: 9000,     // ... with this time constant, turning up to wait_rot_deg about the beam point (back over the crossfade)
  wait_rot_deg: 10,
  wait_r_m: 6000,        // the names and facts: the gazetteer within this of the beam point
  wait_names: 8,         // names popping in at most, one per wait_name_ms
  wait_name_ms: 1100,
  wait_fact_ms: 4500,    // the card's fact line rotates this often
  label_ms: 400,         // ... re-asked at most this often
  card_s: 0.6,           // the arrival card shows until s reaches this
  card_lake_m: 300,      // a lake within this of the landing: its live water temperature on the card
  // THE VR LANDING (owner 2026-10-31 "also for the vr landing"): comfort first
  vr: 'flight',          // 'overlay': the instant fallback (black until the release, as before)
  vr_h_m: 600,           // the start height (m), at least N + sprite_pad
  vr_rate: 0.5,          // the optic flow at most: d ln(height) / dt (1/s) — the ground never rushes up
  vr_vmax: 0.12, vr_accel: 0.06,   // s per second (²): eased in and out
  vr_fade_ms: 1200,      // black → the world, at the start height, standing still
  vr_hurry_ms: 6000,     // from the old release the rest within this (gentler than hurry_ms)
  vr_warm_ms: 20000,     // the old release lifts the black only once the warm-up set is through or this after the latch
  vr_vig_rate: 0.5,      // the comfort vignette is full at this optic flow (1/s)
  // THE SETTLE (w59; owner 2026-11-03 "you could even wait a bit longer before setting off the jump with a half loaded
  // world"): after the terms above the view holds until the near world is resident and the frame rate steady
  // (xrload.js settleTerms; main.js vrSettle) — at most vr_settle_max_ms; a controller button still lands at once
  vr_settle_max_ms: 25000,
  vr_settle_fps: 60,     // frames over the last vr_settle_win_ms at least this rate … (the world draws behind the room:
                         // the Quest's steady play read ~67 (5-s means, 2026-11-03), so 68 would always run to the cap)
  vr_settle_gap_ms: 30,  // … and none longer than this (one dropped 72 Hz frame)
  vr_settle_win_ms: 1000,
  vr_settle_ground: 0,   // on-screen ground tiles with nothing resident under them (holes) at most
  vr_settle_gm: 4,       // GROUND-M frame ('near') nodes queued + in flight at most (far ones wait on the Beam's bulk pause)
  vr_settle_bld: 1,      // building tiles within the ring's cull (the void's D under the VR look) not drawn yet, at most
  vr_settle_trees: 1,    // tree chunks queued at most
  vr_settle_cq: 0,       // compile jobs queued + running at most
});
export function flightCfg(t, cls) {
  const F = { ...FLIGHT_DEF, ...(t && typeof t === 'object' ? t : {}) };
  if (F.tex_slack && typeof F.tex_slack === 'object') F.tex_slack = F.tex_slack[cls] ?? F.tex_slack.default ?? 0;
  return F;
}

const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);
const smooth = (a, b, x) => { const t = b > a ? clamp01((x - a) / (b - a)) : x >= b ? 1 : 0; return t * t * (3 - 2 * t); };

// the height over s (log-space, eased: H at 0, the eye at 1)
export function flightH(s, H, eyeH) { return eyeH * Math.pow(Math.max(H, eyeH) / eyeH, 1 - smooth(0, 1, s)); }
// its inverse (bisection; 0 at or above H)
export function flightSAt(h, H, eyeH) {
  let a = 0, b = 1;
  for (let i = 0; i < 40; i++) { const m = (a + b) / 2; if (flightH(m, H, eyeH) > h) a = m; else b = m; }
  return a;
}
// the landing view covers the camera's (main.js fl.lead): the camera is at
// least as far from its nearest visible ground — the bottom ray's hit, the
// ground (groundAt(dx, dz), relative to the beam feet) re-read there once —
// as the standing eye is; the tree's levels for the standing eye are then at
// least as fine everywhere the camera sees (a straight glide always holds)
export function leadCovers(p, eyeH, vfov, groundAt) {
  const a = -p.pitch + vfov / 2;
  if (a <= 1e-3) return true;                     // no ground under the bottom edge's horizon
  const lx = -Math.sin(p.yaw), lz = -Math.cos(p.yaw);
  let dc = 0, gy = 0;
  for (let i = 0; i < 2; i++) {
    if (p.h - gy <= 0) return false;
    dc = a >= Math.PI / 2 - 1e-6 ? 0 : (p.h - gy) / Math.tan(a);
    gy = groundAt(p.dx + lx * dc, p.dz + lz * dc);
  }
  if (p.h - gy <= 0) return false;
  return Math.hypot(dc, p.h - gy) >= Math.hypot(p.dx + lx * dc, p.dz + lz * dc, eyeH - gy);
}
// the 2D stage's pose: straight down, north-up, above the beam point
export function nadirPose(s, H, eyeH) { return { s, h: flightH(s, H, eyeH), dx: 0, dz: 0, pitch: -Math.PI / 2, yaw: 0 }; }

// THE PATH from the crossfade (s0, top-down at the 2D's framing) to the
// standing eye: the camera swings out of top-down (depression 90° → at most
// glide_deg, yaw north-up → the landing heading yaw1 the short way) by the
// time the height reaches the sprite range N, orbiting so it always looks at
// the beam point from BEHIND the landing heading; it glides in at that
// depression and flares level to pitch1 below flare_m. Clearance: the path's
// height is held at clear_m over the ground's upper bound (o.top(x, z, r):
// m a.s.l. | null) of every later point (a suffix max: it never climbs). o:
// { H, eyeH, yaw1, pitch1, N, s0, top, bx, by, bz }. → { s0, s1, sF, pose(s),
// lift: the most it raised the nominal path (m) }
export function flightPath(F, o) {
  const { H, eyeH } = o, s0 = o.s0 || 0, g = F.glide_deg * Math.PI / 180;
  const sF = flightSAt(F.flare_m, H, eyeH);
  const s1 = Math.min(sF, Math.max(flightSAt(o.N, H, eyeH), s0 + F.turn_min));
  const dy = Math.atan2(Math.sin(o.yaw1), Math.cos(o.yaw1));
  const nom = (s) => {
    const h = flightH(s, H, eyeH), w = s <= s0 ? 0 : smooth(s0, s1, s);
    const th = Math.PI / 2 - (Math.PI / 2 - g) * w, yaw = dy * w;
    const r = Math.max(0, h - eyeH) * Math.cos(th) / Math.sin(th);
    return { h, r, yaw, dx: Math.sin(yaw) * r, dz: Math.cos(yaw) * r };
  };
  // req: the height (over the beam ground) every later point needs — the
  // ground's upper bound + clearance there, a suffix max (the camera holds it
  // until past the ridge, never climbing)
  const n = 256, req = new Float64Array(n + 1).fill(-1e9);
  let lift = 0;
  if (o.top) {
    for (let i = n; i >= 0; i--) {
      const s = s0 + (1 - s0) * i / n, p = nom(s);
      let need = -1e9;
      if (s < sF) {
        const t = o.top(o.bx + p.dx, o.bz + p.dz, p.r);
        if (t !== null && t !== undefined) need = t + Math.min(F.clear_m, 0.5 * (p.h - eyeH)) - o.by;
      }
      req[i] = Math.max(need, i < n ? req[i + 1] : -1e9);
      lift = Math.max(lift, req[i] - p.h);
    }
  }
  const pose = (s) => {
    if (s <= s0) return nadirPose(s, H, eyeH);
    const p = nom(s), u = (s - s0) / (1 - s0) * n, i = Math.min(n - 1, Math.floor(u));
    const h = Math.max(p.h, req[i] + (req[i + 1] - req[i]) * (u - i));
    // looking at the beam point; past the swing never steeper than the glide
    // (a lifted stretch looks past it), then the flare to the landing look
    let look = Math.atan2(h - eyeH, p.r);
    if (s >= s1) look = Math.min(look, g);
    const k = smooth(sF, 1, s);
    return { s, h, dx: p.dx, dz: p.dz, pitch: -look * (1 - k) + o.pitch1 * k, yaw: p.yaw };
  };
  return { s0, s1, sF, pose, lift };
}

// THE VR POSE: straight down over the beam point — the rig is only ever
// translated (no yaw, no pitch, no roll: a fixed horizon, the head always
// free); its look is the head's (look: { pitch, yaw }, main.js flightVr), so
// the readiness test sees what the player sees
export function vrPose(s, H, eyeH, look) { return { s, h: flightH(s, H, eyeH), dx: 0, dz: 0, pitch: look.pitch, yaw: look.yaw }; }
// the s-speed whose optic flow d ln h / dt is `rate` (flightH: ln h falls by
// ln(H / eyeH) · smooth'(s), smooth' = 6 s (1 - s))
export function vrVmax(s, H, eyeH, rate) {
  const k = Math.log(Math.max(H, eyeH) / eyeH) * 6 * s * (1 - s);
  return k > 1e-9 ? rate / k : Infinity;
}

// an in-game beam flies (main.js onPlaced): PC flat play only, never inside
// the entry. ALWAYS on PC (owner 2026-10-31: the iGPU runs 24–27 fps): under
// arc_fps (the smoothed frame gap) at arc_res x the resolution, at EVERY frame
// rate (owner 2026-10-31 live, 12–15 fps: "i want the flying animation too";
// the timed pace copes); under arc_min_m a hop. → null (instant) | { kind 'arc' | 'hop', res }
// the arc's duration (ms) at distance d (m): longer with the distance, clamped;
// at least its apparent motion (arcPath flow, over flat ground) at arc_flow_max
// rad/s + the pace's ramp — a short high arc is never a rush
export function arcDur(d, F) {
  const t = Math.min(F.arc_dur_max_ms, F.arc_dur_min_ms + F.arc_dur_per_km_ms * Math.max(0, d - F.arc_min_m) / 1000);
  const A = arcPath(F, { ox: d, oy: 0, oz: 0, eyeH: 1.6, yaw0: 0, pitch0: 0, pitch1: 0, top: null });
  return Math.max(t, F.ramp_ms + 1000 * A.flow / F.arc_flow_max);
}
export function arcPlan(o, F) {
  if (o.mode !== 'flat' || flightMode(o.mode) !== 'flight' || o.busy || !(o.d >= 1)) return null;
  const fps = o.gapMs > 0 ? 1000 / o.gapMs : Infinity;
  return { kind: o.d < F.arc_min_m ? 'hop' : 'arc', res: fps < F.arc_fps ? F.arc_res : 1 };
}
// THE ARC of an in-game beam, from the old eye (o.ox, o.oy, o.oz: the old feet
// relative to the new feet B) to the standing eye at B: the horizontal travel
// eased, the height a parabola over the straight line (apex arc_k x the
// distance within arc_h_min..arc_h_max), raised as a whole where the ground's
// upper bound + clear_m needs it (o.top(x, z, r): m a.s.l. | null, sampled over
// the middle 80 % of the way); the view turns from the old look (yaw0, pitch0) to the
// travel heading over the first 12 % of the way, looks at the landing point never steeper
// than glide_deg (the sprite fans), and flares to pitch1 at the end. A beam
// under arc_min_m is a HOP: apex hop_k x the distance within
// hop_h_min..hop_h_max, hop_clear_m, the view turning over its first 60 %. o:
// { ox, oy, oz, eyeH, yaw0, pitch0, pitch1, top, bx, by, bz } → { pose(s): s the
// share of the apparent motion (below), yaw1: the travel heading, apex, lift, hop,
// flow: the whole apparent motion (rad) }
export function arcPath(F, o) {
  const { ox, oy, oz, eyeH } = o, d = Math.hypot(ox, oz), g = F.glide_deg * Math.PI / 180, hop = d < F.arc_min_m;
  const apex = hop ? Math.min(F.hop_h_max, Math.max(F.hop_h_min, F.hop_k * d)) : Math.min(F.arc_h_max, Math.max(F.arc_h_min, F.arc_k * d));
  const clear = hop ? F.hop_clear_m : F.clear_m, turn = hop ? 0.6 : 0.12;
  const yaw1 = d > 1e-6 ? Math.atan2(ox, oz) : o.yaw0;
  const dyaw = Math.atan2(Math.sin(yaw1 - o.yaw0), Math.cos(yaw1 - o.yaw0));
  const at = (s) => {
    const u = smooth(0, 1, s), b = 4 * u * (1 - u);
    return { u, b, dx: ox * (1 - u), dz: oz * (1 - u), h0: eyeH + oy * (1 - u) };
  };
  let lift = 0;
  if (o.top) {
    for (let i = 1; i < 64; i++) {
      const s = 0.1 + 0.8 * i / 64, p = at(s);
      const t = o.top(o.bx + p.dx, o.bz + p.dz, 64);
      if (t !== null && t !== undefined && p.b > 0) lift = Math.max(lift, (t + clear - o.by - p.h0) / p.b - apex);
    }
  }
  const pose0 = (s, w) => {
    const p = at(w), h = p.h0 + (apex + lift) * p.b, r = Math.hypot(p.dx, p.dz);
    const look = Math.min(g, Math.max(0, Math.atan2(h - eyeH, r)));
    const k0 = smooth(0, turn, w), k1 = smooth(0.88, 1, w);
    return { s, h, dx: p.dx, dz: p.dz, pitch: (o.pitch0 * (1 - k0) - look * k0) * (1 - k1) + o.pitch1 * k1, yaw: o.yaw0 + dyaw * k0 };
  };
  // THE PERCEIVED PACE (owner 2026-10-31 "it looks like a scene cut … should
  // be smooth, approach and landing"): s is the share of the path's apparent
  // motion — its length over the height above the straight line between the
  // two grounds + arc_flow_m — so an even s pace takes off and lands at the
  // same apparent speed as it crosses the apex: no rush at the ground
  const N = 256, cum = new Float64Array(N + 1);
  let q = at(0), hq = (apex + lift) * q.b;
  for (let i = 1; i <= N; i++) {
    const p = at(i / N), hp = (apex + lift) * p.b;
    const dl = Math.hypot(p.dx - q.dx, p.h0 + hp - q.h0 - hq, p.dz - q.dz);
    cum[i] = cum[i - 1] + dl / (eyeH + (hp + hq) / 2 + F.arc_flow_m);
    q = p; hq = hp;
  }
  const warp = (s) => {
    const c = clamp01(s) * cum[N];
    let lo = 0, hi = N;
    while (hi - lo > 1) { const m = (lo + hi) >> 1; if (cum[m] <= c) lo = m; else hi = m; }
    const span = cum[hi] - cum[lo];
    return (lo + (span > 0 ? (c - cum[lo]) / span : 0)) / N;
  };
  const pose = (s) => pose0(s, warp(s));
  return { pose, yaw1, apex, lift, hop, flow: cum[N] };
}

// THE FLIGHT'S INPUT (flat; owner 2026-10-31 "the exiting to 2d map … needs
// to be stopped until the beaming is completed"): from the Beam until landed
// the 2D Map / the 2D↔3D switch (N, the right button) and the briefing (?)
// are HELD — remembered (the last wins) and run once landed; Space held
// skip_hold_ms lands at once; every other key or click does nothing (fire,
// menus). → { act: 'map' | 'brief' | null, space: 'down' | 'up' | null }
export function flightPress(type, e) {
  const out = { act: null, space: null };
  if (type === 'keydown' || type === 'keyup') {
    if (e.code === 'Space') { if (!e.repeat) out.space = type === 'keydown' ? 'down' : 'up'; return out; }
    if (type === 'keydown' && !e.repeat) out.act = e.code === 'KeyN' ? 'map' : e.key === '?' ? 'brief' : null;
  } else if ((type === 'pointerdown' || type === 'mousedown') && e.button === 2) out.act = 'map';
  return out;
}
// the skip hold's fill 0..1 (at: Space down, null: up); 1 = land
export function skipFill(at, now, F) { return at === null || at === undefined ? 0 : clamp01((now - at) / F.skip_hold_ms); }
// MOUSE LOOK IN A FLIGHT (the path is fixed, the look is free): o = { yaw,
// pitch, at } the offset from the path's look, fed by the mouse's deltas this
// frame (rad), at most look_max_deg; once the mouse rests look_rest_ms it
// springs back (look_spring /s)
export function lookOffset(o, dYaw, dPitch, dt, now, F) {
  const m = F.look_max_deg * Math.PI / 180, cl = (v) => (v < -m ? -m : v > m ? m : v);
  if (dYaw || dPitch) { o.yaw = cl(o.yaw + dYaw); o.pitch = cl(o.pitch + dPitch); o.at = now; }
  else if (now - o.at >= F.look_rest_ms) { const k = Math.exp(-dt * F.look_spring); o.yaw *= k; o.pitch *= k; }
  return o;
}

// THE HANDOFF (owner 2026-10-31 "should be smooth, approach and landing"; bx6
// probe: a 2.18 m step on the entry's control frame): the camera relative to
// the body (the rig stands at pos) is the path from the flight's base, with
// the body's drift from that base (the server's placement, the settle) and its
// eye height joined in over s hand_s..1 — at s = 1 the body's standing eye
// exactly, endFlight's (0, eye, 0): no step at control. out: a Vector3
export function handRel(out, base, pose, pos, eye, eyeH, s, F) {
  const k = smooth(F.hand_s, 1, s), j = 1 - k;
  return out.set(j * (base.x - pos.x) + pose.dx, j * (base.y - pos.y) + pose.h + k * (eye - eyeH), j * (base.z - pos.z) + pose.dz);
}
// the look at control: the path's landing look with the mouse's offset kept
// (lk: lookOffset's; it no longer springs back once the player has control)
export function landLook(yaw1, pitch1, lk) {
  return { yaw: yaw1 + (lk ? lk.yaw : 0), pitch: Math.max(-1.5, Math.min(1.5, pitch1 + (lk ? lk.pitch : 0))) };
}
// THE RESOLUTION'S RETURN (arc_res, look.js LookScale: nothing allocated):
// not at control but once the view has stood still res_still_ms, at least
// res_settle_ms after control; res_max_ms after control at the latest; then
// the scale ramps to 1 over res_ramp_ms (main.js resTick). at, stillAt, now: ms
export function resBack(at, stillAt, now, F) {
  return now - at >= F.res_max_ms || (now - at >= F.res_settle_ms && now - stillAt >= F.res_still_ms);
}

// the landing heading: the yaw (0 = north, the camera's −Z) whose ground rises
// least within R m — the most open view (top: the ground's upper bound)
export function openHeading(top, bx, bz, y, n, R) {
  let best = 0, bestA = Infinity;
  for (let i = 0; i < n; i++) {
    const a = (2 * Math.PI * i) / n, ux = -Math.sin(a), uz = -Math.cos(a);
    let m = -Infinity;
    for (let d = 60; d <= R; d *= 1.25) {
      const t = top(bx + ux * d, bz + uz * d, d);
      if (t !== null && t !== undefined) m = Math.max(m, Math.atan2(t - y, d));
    }
    if (m < bestA) { bestA = m; best = a; }
  }
  return Math.atan2(Math.sin(best), Math.cos(best));
}
// the ground's upper bound at (x, z): the max height (Y1) of the finest
// ground-tree node there whose side is at least minSide (index: GMI1 rows)
export function nodeTop(index, x, z, minSide) {
  if (!index || !index.nodes) return null;
  for (let k = 0; k < 20; k++) {
    const sd = nodeSide(k);
    if (sd < minSide) continue;
    const nd = index.nodes.get(`${k}_${Math.floor((x - 0.25) / sd)}_${Math.floor((z - 0.25) / sd)}`);
    if (nd) return nd.Y1;
    if (sd > 1e5) break;
  }
  return null;
}

// THE PACE: `ok` is the furthest pose a test passed; the camera (s) brakes
// toward it and never passes it. probe() names the next pose to test (ok +
// step: a pass doubles the step, a fail halves it); advance(dt) moves s by a
// DRAWN frame's dt (0 for a frame not drawn or a hitch). `cap`: no probe past
// it (the 2D stage's: h_fade_min). TIMED (timed(sec)): the rest of the path
// over sec at a base speed (eased in and braked into s = 1 over ramp_ms) —
// moving from the first frame; past the bound it slows to pace_floor x the
// base, never stops and never catches up (no snap); hurry() lifts the bound
export class FlightPace {
  constructor(F) { this.F = F; this.s = 0; this.v = 0; this.ok = 0; this.step = F.step0; this.at = -Infinity; this.cap = 1; this.base = 0; }
  timed(sec) {
    const r = this.F.ramp_ms / 1000;
    this.base = (1 - this.s) / Math.max(0.2, sec - r);
    this.acc = this.base / Math.max(0.05, r);
  }
  probe(now) {
    if (this.ok >= this.cap || now - this.at < this.F.test_ms) return null;
    this.at = now;
    return Math.min(this.cap, Math.max(this.ok, this.base ? this.s : 0) + this.step);
  }
  result(c, pass) {
    const F = this.F;
    if (pass) { if (c > this.ok) this.ok = c; this.step = Math.min(F.step_max, this.step * 2); }
    else this.step = Math.max(F.step_min, this.step / 2);
  }
  lift() { this.ok = 1; }                // the bound goes
  // the old overlay's release came: the bound goes and the rest of the descent takes at most `sec`
  hurry(sec) { this.ok = 1; if (!this.base) this.vmin = (1 - this.s) / Math.max(0.1, sec); }
  advance(dt) {
    if (this.base) {
      const b = this.base, a = this.acc;
      const vT = this.s < this.ok - 1e-9 ? b : b * this.F.pace_floor;
      const vDes = Math.max(0.05 * b, Math.min(vT, Math.sqrt(2 * a * (1 - this.s))));
      this.v = vDes > this.v ? Math.min(vDes, this.v + a * dt) : Math.max(vDes, this.v - 2 * a * dt);
      this.s = Math.min(1, this.s + this.v * dt);
      return this.s;
    }
    const F = this.F, gap = Math.max(0, this.ok - this.s), vmin = this.vmin || 0;
    // vmaxAt(s): a speed cap along the path (VR: the optic flow, vrVmax)
    const vmax = this.vmaxAt ? Math.min(F.vmax, this.vmaxAt(this.s)) : F.vmax;
    const vDes = Math.max(vmin, Math.min(vmax, Math.sqrt(2 * F.accel * gap)));
    this.v = vDes > this.v ? Math.min(vDes, this.v + Math.max(F.accel, 2 * vmin) * dt) : vDes;
    this.s = Math.min(this.ok, this.s + this.v * dt);
    return this.s;
  }
}

// the pool's tile key of level j at (x, z) (gmtex texReady's)
function texKey(vt, j, x, z) {
  const L = vt.lv[j];
  return `${j}_${Math.max(0, Math.min(L.nx - 1, Math.floor(x / L.m)))}_${Math.max(0, Math.min(L.ny - 1, Math.floor(z / L.m)))}`;
}
// the finest resident texture level at (x, z) (gmtex: j 0 = finest), -1 none
export function finestTex(vt, x, z) {
  for (let j = 0; j < vt.lv.length; j++) if (vt.res.has(texKey(vt, j, x, z))) return j;
  return -1;
}
// resident level j at (x, z) is coarser than texel `need`: is a level that
// closes the gap (from the coarsest fine enough, the finest level when none
// is, up to j) still to be painted — wanted by the pool, in its HELD SET (the
// cap: what `wanted()` will hand the painter), not failed? What the pool will
// never paint there (past its cap, its radii) is the device's steady state,
// the same the player stands in: nothing to wait for
export function texPending(vt, x, z, j, need) {
  let jn = 0;
  for (let i = 0; i < vt.lv.length; i++) if (vt.lv[i].m / vt.lv[i].px <= need) jn = i;
  const want = vt.want && vt.want.naked, held = vt._held;
  for (let i = jn; i < j; i++) {
    const key = texKey(vt, i, x, z);
    if (want && want.has(key) && (!held || held.has(key)) && !vt.res.has(key) && !(vt.failed && vt.failed.has(key))) return key;
  }
  return null;
}

const _cam = new THREE.PerspectiveCamera(), _m4 = new THREE.Matrix4(), _fr = new THREE.Frustum();
const _box = new THREE.Box3(), _d = new THREE.Vector3();

// THE READINESS TEST of the pose at s. c: { camera (fov / aspect / near / far
// read), base {x, y, z} (the feet at the beam point), eyeH, pose(s) → { h, dx, dz, pitch, yaw },
// tree (GroundTree: index, _opt, _rdy, drawnLevel, vt), march(o, d, maxM) →
// distance | null, pa (rad / px), bld (Buildings) | null, now }.
// Counts on screen: white (no drawn node or no texture where a ray meets the
// ground), tex (the finest resident level's texel > distance · pa · texel_px,
// the pool's own want rule at lead 1, while the pool still paints a level
// that closes it: texPending), geo (a node the pose's own selection
// wants and is not ready, whose geomorph would start above m_tol — the
// parent's error over SEL_PX on screen), bld (a building tile in the ring's
// cull, in view, not drawn). ok = all zero. `cx`: the view centre's need and
// resident (the flight record).
export function flightTest(s, c, F) {
  const p = c.pose(s);
  const cam = _cam, b = c.base;
  cam.fov = c.camera.fov; cam.aspect = c.camera.aspect; cam.near = c.camera.near; cam.far = c.camera.far;
  cam.updateProjectionMatrix();
  cam.position.set(b.x + p.dx, b.y + p.h, b.z + p.dz);
  cam.rotation.set(p.pitch, p.yaw, 0, 'YXZ');
  cam.updateMatrixWorld(true);
  _fr.setFromProjectionMatrix(_m4.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse));
  const out = { s, h: p.h, white: 0, tex: 0, geo: 0, bld: 0, rays: 0, sky: 0, cx: null, texAt: [] };
  const tree = c.tree, vt = tree.vt, kpx = vt.kpx || 1, slack = Math.pow(2, F.tex_slack);
  const ray = (u, v, centre) => {
    _d.set(u, v, 0.5).unproject(cam).sub(cam.position).normalize();
    const t = c.march(cam.position, _d, F.ray_max_m);
    if (t === null || t === undefined || !(t > 0)) { out.sky++; return; }
    out.rays++;
    const x = cam.position.x + _d.x * t, z = cam.position.z + _d.z * t;
    const gl = tree.drawnLevel(x, z), j = finestTex(vt, x, z);
    const need = t * c.pa * kpx * slack;
    const res = j >= 0 ? vt.lv[j].m / vt.lv[j].px : Infinity;
    if (gl < 0 || j < 0) out.white++;
    else if (res > need) {
      const k = texPending(vt, x, z, j, need);
      if (k) {
        out.tex++;
        // the record: what the first rays wait on (key, rank, the painter's score)
        if (out.texAt.length < 4) out.texAt.push([Math.round(x), Math.round(z), Math.round(t), j, k, vt.want.naked.get(k), vt.score ? vt.score(k) : null]);
        if (!out.chain) {
          out.chain = [];
          for (let q = k; q && out.chain.length < 6; q = vtParent(vt.lv, q)) {
            out.chain.push([q, vt.res.has(q) ? 1 : 0, vt.want.naked.get(q) ?? null, vt._held ? (vt._held.has(q) ? 1 : 0) : -1, vt.failed && vt.failed.has(q) ? Math.round(vt.failed.get(q) - c.now) : null, vt._resv && vt._resv.has(q) ? 1 : 0, vt.score ? +vt.score(q).toFixed(2) : null]);
          }
          const w = vt.wanted ? vt.wanted(c.now) : [];
          out.chain.push(['wanted', w.length, w.slice(0, 3).map((e) => [e[0], +e[3].toFixed(2)])]);
        }
      }
    }
    if (centre) out.cx = { d: Math.round(t), gl, tj: j, texNeed: +need.toFixed(3), texRes: Number.isFinite(res) ? res : null };
  };
  ray(0, 0, true);
  for (let iy = 0; iy < F.rays_y; iy++) {
    for (let ix = 0; ix < F.rays_x; ix++) ray(((ix + 0.5) / F.rays_x) * 2 - 1, ((iy + 0.5) / F.rays_y) * 2 - 1, false);
  }
  // the pose's own selection (morph.js drawSet, a fresh state: no fade) from
  // the tree's eye there (the feet under the camera, as main.js feeds it)
  // (no index yet — a first beam pressed before the ground tree's index arrived: nothing of the pose is ready)
  const opt = tree._opt(), eye = { x: b.x + p.dx, y: b.y + p.h - c.eyeH, z: b.z + p.dz };
  const r = tree.index ? drawSet(tree.index, eye, opt, newState(), c.now, tree._rdy) : { miss: [] };
  if (!tree.index) out.geo++;
  const band = LOD_M.band, K = opt.K, px = opt.px;
  for (const key of r.miss) {
    const n = tree.index.nodes.get(key), up = n && n.up;
    if (!up) continue;
    const sd = nodeSide(n.k), x0 = n.tx * sd + 0.25, z0 = n.ty * sd + 0.25;
    const dx = Math.max(x0 - eye.x, 0, eye.x - (x0 + sd)), dz = Math.max(z0 - eye.z, 0, eye.z - (z0 + sd));
    const dyy = Math.max(n.Y0 - eye.y, 0, eye.y - n.Y1);
    const D = up.err * K / px, md = D > 0 ? clamp01((D - Math.hypot(dx, dyy, dz)) / (band * D)) : 1;
    if (md <= F.m_tol) continue;
    _box.min.set(x0, n.Y0, z0); _box.max.set(x0 + sd, n.Y1, z0 + sd);
    if (_fr.intersectsBox(_box)) out.geo++;
  }
  const B = c.bld;
  if (B && B.info && B.info.length && B.lastUpdate.x < 1e8) {
    const tm = B.tileM, half = tm * 0.71;
    for (const t of B.info) {
      const [tx, ty] = t.tile, key = tx + '_' + ty;
      if (B._dead.has(key)) continue;
      if (Math.hypot((tx + 0.5) * tm - B.lastUpdate.x, (ty + 0.5) * tm - B.lastUpdate.z) - half >= B.cull) continue;
      const have = B.tiles.get(key);
      if (have && have !== 'loading' && have.visible) continue;
      _box.min.set(tx * tm, b.y - 1000, ty * tm); _box.max.set((tx + 1) * tm, b.y + 1500, (ty + 1) * tm);
      if (_fr.intersectsBox(_box)) out.bld++;
    }
  }
  out.ok = !out.white && !out.tex && !out.geo && !out.bld;
  return out;
}

// THE 2D STAGE: a canvas over the Map (index.html #beam-cover, the GL
// canvas's rect) painted by the Map's own static painter (PlayerMap
// paintFlight) — from the full Map's view at the click to the beam point at
// the scale of the 3D camera at H (mpp per canvas px), over zoom_ms (the
// beam point's screen position runs to the centre with the zoom); then it
// holds (tiles landing still repaint, ≤ 10 Hz) until fade(). THE MAP WAIT
// (cine, the entry only; beamlabels.js): the zoom ends wait_wide x wider and
// the hold pushes in toward the matched scale (e^(-t / wait_tau_ms)) while
// it turns up to wait_rot_deg about the beam point; settle(ms) eases both
// back to the matched view over the crossfade — at its end the cover is the
// 3D's framing exactly. deco(g, w, h, v, now): drawn over each frame
export class FlightCover {
  constructor(el, map) { this.el = el; this.map = map; this._raf = 0; this.zoomed = false; this._at = 0; this.mpp = 0; this.c = null; this.deco = null; }
  start(v0, to, ms) {
    const el = this.el;
    this.v0 = { cx: v0.cx, cz: v0.cz, mpp: v0.mpp };
    this.to = to; this.ms = ms; this.t0 = performance.now(); this.zoomed = false; this.tz = null; this.st = null;
    el.style.transition = ''; el.style.opacity = '1'; el.hidden = false;
    const loop = (t) => { this._raf = requestAnimationFrame(loop); this._frame(performance.now(), t); };
    this._raf = requestAnimationFrame(loop);
    this._frame(this.t0);                       // drawn now: no frame of what lies under it first
  }
  // the inverse beam's end (3D → Map): the still view v fades in over ms, then done
  reveal(v, ms, done) {
    this.start(v, v, 1);
    const el = this.el;
    el.style.opacity = '0';
    void el.offsetWidth;                        // the start opacity applied before the transition
    el.style.transition = `opacity ${ms}ms ease-in-out`;
    el.style.opacity = '1';
    setTimeout(done, ms + 30);
  }
  // the 2D stage's descent once zoomed (hold: the paced s's height → its scale)
  track(mpp) { this.mpp = mpp; }
  // the wait's move: c { wide, tau_ms, rot (rad) } (call before start), or null
  cine(c) { this.c = c && c.wide >= 1 && c.tau_ms > 0 ? c : null; }
  // the crossfade: the move eases back to the matched view over ms
  settle(ms, now = performance.now()) { if (this.c && !this.st) this.st = { at: now, ms, w: this._w(now), r: this._r(now) }; }
  // the move's scale factor and turn by now (1, 0 without one)
  _w(now) { return this.c && this.tz !== null ? 1 + (this.c.wide - 1) * Math.exp(-(now - this.tz) / this.c.tau_ms) : this.c ? this.c.wide : 1; }
  _r(now) { return this.c && this.tz !== null ? this.c.rot * (1 - Math.exp(-(now - this.tz) / this.c.tau_ms)) : 0; }
  view(now) {
    const { v0, to } = this, k = smooth(0, 1, (now - this.t0) / this.ms);
    if (this.zoomed) {
      if (this.tz === null) this.tz = now;
      let w = this._w(now), r = this._r(now);
      if (this.st) { const u = smooth(0, 1, (now - this.st.at) / this.st.ms); w = 1 + (this.st.w - 1) * (1 - u); r = this.st.r * (1 - u); }
      const m = (this.mpp > 0 ? this.mpp : to.mpp) * w;
      return { cx: to.cx, cz: to.cz, mpp: m, rot: r, anim: m !== this._mpp || r !== this._rot, k: 1 };
    }
    const tm = to.mpp * (this.c ? this.c.wide : 1);
    const mpp = Math.exp(Math.log(v0.mpp) * (1 - k) + Math.log(tm) * k);
    const f = Math.abs(v0.mpp - tm) > 1e-9 ? (mpp - tm) / (v0.mpp - tm) : 1 - k;
    return { cx: to.cx + (v0.cx - to.cx) * f, cz: to.cz + (v0.cz - to.cz) * f, mpp, rot: 0, anim: k < 1, k };
  }
  _frame(now) {
    const v = this.view(now);
    if (this.zoomed && !v.anim && now - this._at < 100) return;
    this._at = now; this._mpp = v.mpp; this._rot = v.rot; this.v = v;   // v: the last painted view (the wait's names)
    const cv = this.el, d = Math.min(devicePixelRatio || 1, 2);
    const w = Math.round(cv.clientWidth * d), h = Math.round(cv.clientHeight * d);
    if (cv.width !== w || cv.height !== h) { cv.width = w; cv.height = h; }
    if (this.c) v.bare = true;                  // the wait's names are its own (beamlabels.js): no Map labels under them
    const g = cv.getContext('2d');
    this.map.paintFlight(g, w, h, v, now);
    if (this.deco) this.deco(g, w, h, v, now);
    if (v.k >= 1) this.zoomed = true;
  }
  fade(ms, done) {
    const el = this.el;
    el.style.transition = `opacity ${ms}ms ease-in-out`;
    el.style.opacity = '0';
    setTimeout(() => { this.stop(); if (done) done(); }, ms + 30);
  }
  stop() {
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = 0;
    this.el.hidden = true;
    this.el.style.opacity = '1';
  }
}
